import { AVOID_TYPES } from './territories/TerritoriesFromJson';

export const AVOID_FEATURES = [
  'tollRoad',
  'motorway',
  'ferry',
  'tunnel',
  'dirtRoad',
  'controlledAccessHighway',
  'carShuttleTrain',
  'seasonalClosure',
  'uTurns',
];

export const TRUCK_ONLY_AVOID_FEATURES = ['difficultTurns'];

export const AVOID_TRUCK_ROAD_TYPES = [
  'ET4',
  'ET2',
  'A4',
  'A2',
  'B4',
  'B2',
  'C',
  'D',
  'BK1',
  'BK2',
  'BK3',
  'BK4',
];

export const getAvoidFeaturesForFleetType = (fleetType) =>
  fleetType === 'truck' ? [...AVOID_FEATURES, ...TRUCK_ONLY_AVOID_FEATURES] : AVOID_FEATURES;

export const isValidAvoidCoordinate = ({ lat, lng }) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

export const parseBoundingBoxArea = ({ north, south, east, west }) => {
  const parsedNorth = parseFloat(north);
  const parsedSouth = parseFloat(south);
  const parsedEast = parseFloat(east);
  const parsedWest = parseFloat(west);

  if (![parsedNorth, parsedSouth, parsedEast, parsedWest].every(Number.isFinite)) return null;
  if (parsedNorth === parsedSouth || parsedEast === parsedWest) return null;

  return {
    type: AVOID_TYPES.BOUNDING_BOX,
    north: Math.max(parsedNorth, parsedSouth),
    south: Math.min(parsedNorth, parsedSouth),
    east: Math.max(parsedEast, parsedWest),
    west: Math.min(parsedEast, parsedWest),
  };
};

export const parsePolygonAreaInput = (text) => {
  if (!text || !text.trim()) return null;

  const outer = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [lat, lng] = line.split(',').map((value) => parseFloat(value.trim()));
      return { lat, lng };
    });

  if (outer.length < 3 || !outer.every(isValidAvoidCoordinate)) return null;

  return { type: AVOID_TYPES.POLYGON, outer };
};

export const getAvoidAreaSummary = (area) => {
  if (!area) return '';

  if (area.type === AVOID_TYPES.BOUNDING_BOX)
    return `bbox ${area.north.toFixed(3)},${area.west.toFixed(3)} \u2192 ${area.south.toFixed(
      3,
    )},${area.east.toFixed(3)}`;

  if (area.type === AVOID_TYPES.POLYGON) return `polygon (${area.outer.length} pts)`;

  return area.type;
};

export const AVOID_SEGMENT_DIRECTIONS = ['both', 'forward', 'backward'];

const SEGMENT_DIRECTION_SUFFIX = { both: '*', forward: '+', backward: '-' };
const SEGMENT_SUFFIX_DIRECTION = { '*': 'both', '+': 'forward', '-': 'backward' };

export const formatAvoidSegment = ({ id, direction = 'both' }) =>
  `${id}#${SEGMENT_DIRECTION_SUFFIX[direction] || SEGMENT_DIRECTION_SUFFIX.both}`;

export const parseAvoidSegment = (value) => {
  if (!value) return null;
  const [id, suffix] = value.split('#');
  return { id, direction: SEGMENT_SUFFIX_DIRECTION[suffix] || 'both' };
};

export const buildAvoidPayload = ({ areas = [], features = [], segments = [] }) => {
  const payload = {};
  if (areas.length) payload.areas = areas;
  if (features.length) payload.features = features;
  if (segments.length) payload.segments = segments.map(formatAvoidSegment);
  return Object.keys(payload).length ? payload : null;
};

export const buildTruckRoadTypesPayload = ({ truckRoadTypes = [] }) => {
  const payload = {};
  if (truckRoadTypes.length) payload.truckRoadTypes = truckRoadTypes;
  return Object.keys(payload).length ? payload : null;
};

export const AVOID_ZONE_CATEGORIES = ['environmental', 'congestionPricing', 'vignette'];

export const buildAvoidZonesPayload = ({
  categories = [],
  exceptZoneIds = [],
  zoneIdentifiers = [],
}) => {
  const payload = {};
  if (categories.length) {
    payload.zoneCategories = exceptZoneIds.length ? { categories, exceptZoneIds } : { categories };
  }
  if (zoneIdentifiers.length) payload.zoneIdentifiers = zoneIdentifiers;
  return Object.keys(payload).length ? payload : null;
};

export const buildExcludePayload = ({ countries = [] }) => {
  const payload = {};
  if (countries.length) payload.countries = countries;
  return Object.keys(payload).length ? payload : null;
};

export const TRAFFIC_MODES = ['default', 'disabled'];
export const DEFAULT_TRAFFIC_MODE = 'default';

export const IGNORE_ROUTE_VIOLATIONS_OPTIONS = ['all', 'traffic'];

export const toDepartureTimeIso = (localDateTime) => {
  if (!localDateTime) return '';
  const parsed = new Date(localDateTime);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

export const fromDepartureTimeIso = (isoDateTime) => {
  if (!isoDateTime) return '';
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (value) => String(value).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(
    parsed.getHours(),
  )}:${pad(parsed.getMinutes())}`;
};

export const buildTrafficRoutingPayload = ({
  trafficMode = DEFAULT_TRAFFIC_MODE,
  departureTime = '',
  ignoreRouteViolations = [],
}) => {
  const payload = {};
  if (trafficMode && trafficMode !== DEFAULT_TRAFFIC_MODE) payload.traffic = { mode: trafficMode };
  const departureTimeIso = toDepartureTimeIso(departureTime);
  if (departureTimeIso) payload.departureTime = departureTimeIso;
  if (ignoreRouteViolations.length) payload.ignoreRouteViolations = ignoreRouteViolations;
  return Object.keys(payload).length ? payload : null;
};
